import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { InviteAcceptResult, MyInvite, MyInvitesResponse } from '../api/types'
import { useSession } from '../context/SessionContext'

const ROLE_LABEL: Record<string, string> = { admin: 'an admin', member: 'a member', viewer: 'a viewer' }

// In-app notice for pending invites addressed to the signed-in email (GET /me/invites).
// Invites to an existing account have no email (D35), so this is how they are found.
export function InviteBanner() {
  const queryClient = useQueryClient()
  const { switchWorkspace } = useSession()
  const [dismissed, setDismissed] = useState(false)
  const [joined, setJoined] = useState<{ id: string; name: string } | null>(null)
  const { data } = useQuery({
    queryKey: qk.myInvites,
    queryFn: () => apiFetch<MyInvitesResponse>('/me/invites'),
    staleTime: Infinity, // once per session; refetched after an accept
    retry: false,
  })

  const accept = useMutation({
    mutationFn: (invite: MyInvite) =>
      apiFetch<InviteAcceptResult>(`/me/invites/${encodeURIComponent(invite.id)}/accept`, { method: 'POST' }),
    onSuccess: (res, invite) => {
      setJoined({ id: res.workspace_id, name: invite.workspace_name })
      queryClient.setQueryData<MyInvitesResponse>(qk.myInvites, (old) =>
        old ? { invites: old.invites.filter((i) => i.id !== invite.id) } : old,
      )
      void queryClient.invalidateQueries({ queryKey: qk.myInvites })
      void queryClient.invalidateQueries({ queryKey: qk.workspaces })
    },
  })

  const invites = data?.invites ?? []
  if (dismissed || (invites.length === 0 && !joined)) return null

  return (
    <section className="invite-banner" aria-label="Workspace invites">
      <div className="invite-banner-body">
        {joined && (
          <p role="status" className="invite-banner-row">
            <span>You joined <strong>{joined.name}</strong>.</span>
            <button type="button" className="btn btn--sm" onClick={() => void switchWorkspace(joined.id)}>
              Open workspace
            </button>
          </p>
        )}
        {invites.map((inv) => (
          <p key={inv.id} className="invite-banner-row">
            <span>
              You’re invited to join <strong>{inv.workspace_name}</strong> as {ROLE_LABEL[inv.role] ?? inv.role}
              {inv.invited_by_name ? <> by {inv.invited_by_name}</> : null}.
            </span>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={accept.isPending}
              aria-busy={accept.isPending && accept.variables?.id === inv.id}
              onClick={() => accept.mutate(inv)}
            >
              {accept.isPending && accept.variables?.id === inv.id ? 'Accepting…' : 'Accept'}
            </button>
          </p>
        ))}
        {accept.isError && <p role="alert" className="form-error">{errorMessage(accept.error)}</p>}
      </div>
      <button type="button" className="btn btn--sm" aria-label="Dismiss invites" onClick={() => setDismissed(true)}>
        Dismiss
      </button>
    </section>
  )
}
